import { Component, OnInit, inject } from '@angular/core';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {DataService} from "../../services/data.service";
import {AccessService} from "../../services/access.service";
import {BackupService} from "../../services/backup.service";
import {ToastrService} from "ngx-toastr";
import Swal from "sweetalert2";
import {ViewMode} from "../../interfaces";
import {NgIf} from "@angular/common";

@Component({
    selector: 'app-country-detail',
    imports: [NgIf, ReactiveFormsModule],
    template: `
        <div class="card">
            <div class="card-body">
                <div class="mb-4">
                    <h3>
                        <i class="fa-solid fa-globe me-2"></i>
                        @switch (viewMode) {
                            @case (ViewMode.INSERT) {
                                Land anlegen
                            }
                            @case (ViewMode.EDIT) {
                                Land bearbeiten
                            }
                            @case (ViewMode.VIEW) {
                                Land
                            }
                        }
                    </h3>
                </div>

                <form [formGroup]="form">
                    <div class="row">
                        <div class="col">
                            <label>Name</label>
                            <input type="text" class="form-control" formControlName="name" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW">{{ form.value.name }}</p>
                        </div>
                        <div class="col">
                            <label>Währung</label>
                            <input type="text" class="form-control" formControlName="currency" placeholder="EUR" [maxlength]="3" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW">{{ form.value.currency }}</p>
                        </div>
                        <div class="col">
                            <label>Code</label>
                            <input type="text" class="form-control" formControlName="code" [maxlength]="2" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW">{{ form.value.code }}</p>
                        </div>
                    </div>
                </form>

                <div class="mt-3">
                    @if (viewMode == ViewMode.VIEW) {
                        <button type="button" class="btn btn-primary me-2" (click)="setMode(ViewMode.EDIT)">Bearbeiten</button>
                        <button type="button" class="btn btn-secondary" (click)="cancel()">Zurück</button>
                        <button type="button" class="btn btn-danger float-end" (click)="delete()" [disabled]="country?.stores != '0'">Löschen</button>
                    } @else if (viewMode == ViewMode.EDIT) {
                        <button type="button" class="btn btn-primary" [disabled]="form.invalid" (click)="update()">Aktualisieren</button>
                        <button type="button" class="btn btn-secondary ms-2" (click)="cancelEdit()">Abbrechen</button>
                    } @else if (viewMode == ViewMode.INSERT) {
                        <button type="button" class="btn btn-primary" [disabled]="form.invalid" (click)="save()">Speichern</button>
                        <button type="button" class="btn btn-secondary ms-2" (click)="cancel()">Abbrechen</button>
                    }
                </div>

            </div>
        </div>
    `
})
export class CountryDetailComponent implements OnInit {

    accessService = inject(AccessService);
    private router = inject(Router);
    private fb = inject(UntypedFormBuilder);
    private dataService = inject(DataService);
    private route = inject(ActivatedRoute);
    private backupService = inject(BackupService);
    private toastrService = inject(ToastrService);

    ViewMode = ViewMode;

    form!: UntypedFormGroup;
    id_country!: string;
    viewMode!: ViewMode;
    country: any;

    ngOnInit(): void {
        this.id_country = this.route.snapshot.paramMap.get('id') ?? '0';
        this.viewMode = this.id_country == '0' ? ViewMode.INSERT : ViewMode.VIEW;

        this.form = this.fb.group({
            id: [null],
            name: [null, Validators.required],
            currency: [null, Validators.required],
            code: [null, Validators.required],
        });

        if (this.id_country !== '0') {
            this.getCountry();
        }
    }

    getCountry(): void {
        this.dataService.request('Country/get', {
            id_country: this.id_country
        }).subscribe(response => {
            this.country = response.country;

            this.patchForm();
        });
    }

    patchForm(): void {
        this.form.patchValue({
            id: this.country.id,
            name: this.country.name,
            currency: this.country.currency,
            code: this.country.code,
        });
    }

    save(): void {
        this.dataService.request('Country/save', {
            country: this.form.getRawValue(),
        }).subscribe(() => {
            this.router.navigate(['/country']);
            this.toastrService.success('Das Land wurde angelegt');
        });
    }

    update(): void {
        this.dataService.request('Country/update', {
            country: this.form.getRawValue(),
        }).subscribe(() => {
            this.setMode(ViewMode.VIEW);
            this.getCountry();
            this.toastrService.success('Das Land wurde aktualisiert');
        });
    }

    cancel(): void {
        this.router.navigate(['/country']);
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
        this.getCountry();
    }

    delete(): void {
        Swal.fire({
            title: 'Sicher?',
            text: 'Das Land wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('Country/delete', {
                    id_country: this.id_country
                }).subscribe(() => {
                    this.cancel();
                    this.toastrService.success('Das Land wurde gelöscht');
                });
            }
        });
    }

}
